/**
 * Organisation Onboarding Validation
 * 
 * Client-side validation for the org onboarding form.
 * Validates all fields and returns a map of field → error message.
 * Returns empty object if all fields are valid.
 */

import { normalizeIndianMobile } from './phone';

/**
 * Validate the full organisation onboarding form.
 *
 * @param {Object} formData - Form state object
 * @returns {Object} errors - Map of fieldName → error message (empty = valid)
 */
export function validateOrgOnboarding(formData) {
  const errors = {};

  // ── Section 1: Organisation Details ──────────────────────────────

  // First name
  if (!formData.first_name?.trim()) {
    errors.first_name = 'First name is required';
  } else if (formData.first_name.trim().length < 2) {
    errors.first_name = 'First name must be at least 2 characters';
  } else if (formData.first_name.trim().length > 50) {
    errors.first_name = 'First name must be under 50 characters';
  }

  // Last name
  if (!formData.last_name?.trim()) {
    errors.last_name = 'Last name is required';
  } else if (formData.last_name.trim().length < 2) {
    errors.last_name = 'Last name must be at least 2 characters';
  } else if (formData.last_name.trim().length > 50) {
    errors.last_name = 'Last name must be under 50 characters';
  }

  // Organisation name
  if (!formData.organisation_name?.trim()) {
    errors.organisation_name = 'Organisation name is required';
  } else if (formData.organisation_name.trim().length < 2) {
    errors.organisation_name = 'Name must be at least 2 characters';
  } else if (formData.organisation_name.trim().length > 100) {
    errors.organisation_name = 'Name must be under 100 characters';
  }

  // City
  if (!formData.city?.trim()) {
    errors.city = 'City is required';
  } else if (formData.city.trim().length < 2) {
    errors.city = 'City must be at least 2 characters';
  }

  // State
  if (!formData.state?.trim()) {
    errors.state = 'State is required';
  }

  // Mobile number
  if (!formData.mobile_number?.trim()) {
    errors.mobile_number = 'Mobile number is required';
  } else {
    try {
      normalizeIndianMobile(formData.mobile_number);
    } catch (e) {
      errors.mobile_number = e.message || 'Invalid mobile number';
    }
  }

  // Email
  if (!formData.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  // ── Section 2: Operating Details ─────────────────────────────────

  // Working days
  if (!formData.working_days || formData.working_days.length === 0) {
    errors.working_days = 'Select at least one working day';
  }

  // Opening time
  if (!formData.opening_time) {
    errors.opening_time = 'Opening time is required';
  }

  // Closing time
  if (!formData.closing_time) {
    errors.closing_time = 'Closing time is required';
  }

  // Opening < Closing check
  if (formData.opening_time && formData.closing_time) {
    if (formData.opening_time >= formData.closing_time) {
      errors.closing_time = 'Closing time must be after opening time';
    }
  }

  return errors;
}

/**
 * Simple email validation (RFC-ish, not overly strict)
 * @param {string} email
 * @returns {boolean}
 */
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Check if form has any validation errors
 * @param {Object} errors - Validation errors object
 * @returns {boolean}
 */
export function hasErrors(errors) {
  return Object.keys(errors).length > 0;
}
